import React, { useEffect, useState } from 'react';
import { useConfiguration } from 'src/hooks/Configuration';
import { useToast } from 'src/hooks/Toast';
import { useFinanceCheck, FinanceReport, ChargebackMatch, NotConciliatedSale, FinanceItem, SaleItem } from './useFinanceCheck';
import handleFinanceReportPDF, { handleFinanceReportPDFWithSaque } from '../../handleFinanceReportPDF';
import downloadit from 'src/utils/download';
import api from 'src/services/api';
import { FaFilePdf, FaFileExcel, FaWallet } from 'react-icons/fa';
import { useTransferPopup } from './popupTransfers';

// ─────────────────────────────────────────────────────────────────────────────
//  Formatação
// ─────────────────────────────────────────────────────────────────────────────

const fmt = (cents: number) =>
  (cents / 100).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

const fmtDate = (d?: string | null) =>
  d ? new Date(d).toLocaleDateString('pt-BR') : '—';

const fmtDateTime = (d?: string | null) =>
  d ? new Date(d).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' }) : '—';

// ─────────────────────────────────────────────────────────────────────────────
//  Design tokens
// ─────────────────────────────────────────────────────────────────────────────

const C = {
  bg:        '#F4F6FA',
  surface:   '#FFFFFF',
  border:    '#E3E8F0',
  text:      '#1A2236',
  muted:     '#8693A8',
  blue:      '#1E3A5F',
  blueLight: '#EBF1FA',
  green:     '#0A7A55',
  greenBg:   '#E6F7F2',
  red:       '#C0392B',
  redBg:     '#FDECEA',
  amber:     '#92400E',
  amberBg:   '#FEF3C7',
  purple:    '#5B21B6',
  purpleBg:  '#EDE9FE',
};

// ─────────────────────────────────────────────────────────────────────────────
//  Primitivos
// ─────────────────────────────────────────────────────────────────────────────

const Card = ({ children, style }: { children: React.ReactNode; style?: React.CSSProperties }) => (
  <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 12, overflow: 'hidden', ...style }}>
    {children}
  </div>
);

const CardHeader = ({ label, accent = C.blue, right }: { label: string; accent?: string; right?: React.ReactNode }) => (
  <div style={{ padding: '14px 20px', borderBottom: `1px solid ${C.border}`, display: 'flex', alignItems: 'center', gap: 10 }}>
    <div style={{ width: 4, height: 18, borderRadius: 2, background: accent, flexShrink: 0 }} />
    <span style={{ fontWeight: 700, fontSize: 13, color: C.text, letterSpacing: '-0.01em', flex: 1 }}>{label}</span>
    {right}
  </div>
);

const Stat = ({
  label, value, sub, accent = C.blue, size = 'md',
}: {
  label: string; value: string; sub?: string; accent?: string; size?: 'sm' | 'md' | 'lg';
}) => (
  <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
    <div style={{ fontSize: 10, fontWeight: 700, color: C.muted, textTransform: 'uppercase', letterSpacing: '0.07em' }}>{label}</div>
    <div style={{ fontSize: size === 'lg' ? 22 : size === 'md' ? 17 : 14, fontWeight: 800, color: accent, fontVariantNumeric: 'tabular-nums', letterSpacing: '-0.02em' }}>
      {value}
    </div>
    {sub && <div style={{ fontSize: 11, color: C.muted }}>{sub}</div>}
  </div>
);

const DreRow = ({
  label, value, sign, partial, color, indent, subtotal, highlight,
}: {
  label: string; value: number; sign?: '+' | '−' | '+/−'; partial?: number;
  color?: string; indent?: boolean; subtotal?: boolean; highlight?: boolean;
}) => {
  if (subtotal || highlight) {
    return (
      <div style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        padding: '11px 20px',
        background: highlight ? C.blue : '#EEF2F8',
        borderTop: `2px solid ${highlight ? C.blue : '#C8D3E8'}`,
        borderBottom: highlight ? undefined : `1px solid ${C.border}`,
      }}>
        <span style={{ fontWeight: 700, fontSize: 13, color: highlight ? '#fff' : C.text }}>{label}</span>
        <span style={{ fontWeight: 800, fontSize: 15, fontVariantNumeric: 'tabular-nums', color: highlight ? (value >= 0 ? '#6EE7B7' : '#FCA5A5') : (value >= 0 ? C.green : C.red) }}>
          {value < 0 ? `−${fmt(Math.abs(value))}` : fmt(value)}
        </span>
      </div>
    );
  }

  const displayValue = value === 0 ? '—'
    : sign === '−' ? `−${fmt(value)}`
    : sign === '+/−' ? (value > 0 ? `+${fmt(value)}` : value < 0 ? `−${fmt(Math.abs(value))}` : '—')
    : fmt(value);

  return (
    <div style={{
      display: 'grid', gridTemplateColumns: '1fr auto auto', alignItems: 'center',
      gap: '0 16px', padding: '9px 20px', paddingLeft: indent ? 36 : 20,
      borderBottom: `1px solid ${C.border}`,
    }}>
      <span style={{ fontSize: 12.5, color: value === 0 ? C.muted : C.text }}>
        {sign && <span style={{ color: C.muted, marginRight: 6, fontWeight: 700, fontSize: 11 }}>({sign})</span>}
        {label}
      </span>
      <span style={{ fontWeight: 600, fontSize: 13, textAlign: 'right', minWidth: 110, fontVariantNumeric: 'tabular-nums', color: color ?? (value === 0 ? C.muted : C.text) }}>
        {displayValue}
      </span>
      {partial !== undefined && (
        <span style={{ fontSize: 11, color: C.muted, textAlign: 'right', minWidth: 110, fontVariantNumeric: 'tabular-nums' }}>
          {partial < 0 ? `−${fmt(Math.abs(partial))}` : fmt(partial)}
        </span>
      )}
    </div>
  );
};

const Table = ({ headers, children }: { headers: string[]; children: React.ReactNode }) => (
  <div style={{ overflowX: 'auto' }}>
    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12.5 }}>
      <thead>
        <tr>
          {headers.map((h, i) => (
            <th key={i} style={{ padding: '9px 14px', background: '#F1F5FB', color: C.muted, fontWeight: 700, fontSize: 10.5, letterSpacing: '0.06em', textTransform: 'uppercase', textAlign: 'left', borderBottom: `1px solid ${C.border}`, whiteSpace: 'nowrap' }}>
              {h}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>{children}</tbody>
    </table>
  </div>
);

const Td = ({ children, align = 'left', muted = false, nowrap = false, color, bold }: {
  children: React.ReactNode; align?: string; muted?: boolean; nowrap?: boolean; color?: string; bold?: boolean;
}) => (
  <td style={{ padding: '9px 14px', borderBottom: `1px solid #F0F2F7`, color: color ?? (muted ? C.muted : C.text), textAlign: align as any, whiteSpace: nowrap ? 'nowrap' : undefined, fontWeight: bold ? 700 : undefined }}>
    {children}
  </td>
);

const Pill = ({ label, color, bg }: { label: string; color: string; bg: string }) => (
  <span style={{ display: 'inline-block', padding: '2px 9px', borderRadius: 999, fontSize: 10.5, fontWeight: 700, color, background: bg, letterSpacing: '0.04em', textTransform: 'uppercase' }}>
    {label}
  </span>
);

const TabBtn = ({ label, active, badge, onClick }: { label: string; active: boolean; badge?: number; onClick: () => void }) => (
  <button onClick={onClick} style={{ padding: '7px 16px', borderRadius: 8, border: 'none', background: active ? C.blue : 'transparent', color: active ? '#fff' : C.muted, fontWeight: 600, fontSize: 13, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6 }}>
    {label}
    {badge !== undefined && badge > 0 && (
      <span style={{ background: active ? 'rgba(255,255,255,0.25)' : C.red, color: '#fff', borderRadius: 999, fontSize: 10, fontWeight: 800, padding: '1px 6px' }}>
        {badge}
      </span>
    )}
  </button>
);

// ─────────────────────────────────────────────────────────────────────────────
//  Banner de conciliação
// ─────────────────────────────────────────────────────────────────────────────

const ReconciliationBanner = ({ r }: { r: FinanceReport }) => {
  const diff = r.totalSalesBruto - r.brutoConciliated;
  const ok   = diff === 0;

  return (
    <div style={{
      display: 'flex', alignItems: 'flex-start', gap: 14, padding: '14px 20px',
      background: ok ? C.greenBg : C.amberBg,
      border: `1px solid ${ok ? '#6EE7B7' : '#FCD34D'}`,
      borderRadius: 10,
    }}>
      <span style={{ fontSize: 22, lineHeight: 1 }}>{ok ? '✅' : '⚠️'}</span>
      <div style={{ flex: 1 }}>
        <div style={{ fontWeight: 700, fontSize: 13, color: ok ? C.green : C.amber, marginBottom: 8 }}>
          {ok ? 'Bruto das vendas bate com o extrato' : 'Divergência entre vendas pagas e extrato'}
        </div>
        <div style={{ display: 'flex', gap: 32, flexWrap: 'wrap' }}>
          <Stat label="Bruto vendas paidas (plataforma)" value={fmt(r.totalSalesBruto)} accent={C.text} size="sm" sub="Σ payment_status=paid, método pix/boleto/cc" />
          <Stat label="Bruto conciliado (extrato)" value={fmt(r.brutoConciliated)} accent={C.text} size="sm" sub="Σ operation_type_raw=credit" />
          <Stat
            label="Diferença"
            value={diff === 0 ? 'R$ 0,00' : (diff > 0 ? `+${fmt(diff)}` : `−${fmt(Math.abs(diff))}`)}
            accent={ok ? C.green : C.red}
            size="sm"
            sub={diff > 0 ? 'Divergência' : diff < 0 ? 'Divergência' : 'Valores idênticos'}
          />
        </div>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
//  Bloco de Chargebacks — capa  (NOVO)
// ─────────────────────────────────────────────────────────────────────────────

const ChargebackSummary = ({ r }: { r: FinanceReport }) => {
  if (r.chargebacks.length === 0) return null;

  const totalBruto    = r.chargebacks.reduce((a, c) => a + c.saleBruto, 0);
  const totalLiquido  = r.chargebacks.reduce((a, c) => a + c.creditoLiquido, 0);
  const totalDebitado = r.chargebacks.reduce((a, c) => a + c.debitadoChargeback, 0);
  const diffBruto     = totalDebitado - totalBruto;
  const diffLiquido   = totalDebitado - totalLiquido;

  const metrics = [
    { label: 'Bruto recebido (vendas)',        value: totalBruto,    color: C.text,  sign: null,   desc: 'Valor original cobrado do cliente',           highlight: false },
    { label: 'Líquido creditado',              value: totalLiquido,  color: C.green, sign: null,   desc: 'Após taxas da plataforma',                    highlight: false },
    { label: 'Total debitado (chargebacks)',   value: totalDebitado, color: C.red,   sign: '−',    desc: 'Valor estornado pela operadora',              highlight: false },
    { label: 'Δ Debitado vs Bruto',            value: diffBruto,     color: diffBruto  > 0 ? C.red : C.green, sign: '+/−', desc: diffBruto  > 0 ? 'Você perdeu mais que o bruto' : 'Dentro do esperado', highlight: true },
    { label: 'Δ Debitado vs Líquido',          value: diffLiquido,   color: diffLiquido > 0 ? C.red : C.green, sign: '+/−', desc: diffLiquido > 0 ? 'Você perdeu mais que o líquido' : 'Dentro do esperado', highlight: true },
  ] as const;

  return (
    <Card>
      <CardHeader
        label={`⚠️ ${r.chargebacks.length} Chargeback${r.chargebacks.length !== 1 ? 's' : ''} — Resumo de Impacto`}
        accent={C.red}
        right={
          <span style={{ fontSize: 12, color: C.muted }}>
            Prejuízo total: <strong style={{ color: C.red }}>{fmt(r.totalPrejuizoChargebacks)}</strong>
          </span>
        }
      />

      {/* Grid de métricas */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))', borderBottom: `1px solid ${C.border}` }}>
        {metrics.map((m, i) => (
          <div key={i} style={{
            padding: '16px 20px',
            borderRight: i < metrics.length - 1 ? `1px solid ${C.border}` : undefined,
            background: m.highlight ? (m.color === C.red ? C.redBg : C.greenBg) : C.surface,
          }}>
            <div style={{ fontSize: 10, fontWeight: 700, color: C.muted, textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 4 }}>{m.label}</div>
            <div style={{ fontSize: 18, fontWeight: 800, color: m.color, fontVariantNumeric: 'tabular-nums', letterSpacing: '-0.02em' }}>
              {m.sign === '−'   ? '−' : ''}
              {m.sign === '+/−' ? (m.value > 0 ? '+' : m.value < 0 ? '−' : '') : ''}
              {fmt(Math.abs(m.value))}
            </div>
            <div style={{ fontSize: 11, color: C.muted, marginTop: 3 }}>{m.desc}</div>
          </div>
        ))}
      </div>

      {/* Tabela linha a linha */}
      <Table headers={['Data', 'Cliente', 'Método', 'Bruto', 'Líquido', 'Debitado', 'Δ vs Bruto', 'Δ vs Líquido', 'Custo Extra']}>
        {r.chargebacks.map((cb, i) => {
          const dBruto   = cb.debitadoChargeback - cb.saleBruto;
          const dLiquido = cb.debitadoChargeback - cb.creditoLiquido;
          const cliente  = cb.sale?.payer_id_response?.name ?? cb.sale?.client_id_response?.name ?? '—';
          return (
            <tr key={i} style={{ background: '#FFF5F5' }}>
              <Td nowrap muted>{fmtDate(cb.chargebackEntry.date)}</Td>
              <Td bold>{cliente}</Td>
              <Td muted>{cb.chargebackEntry.payment_method_id || cb.sale?.payment_method_id || '—'}</Td>
              <Td align="right">{cb.saleBruto > 0 ? fmt(cb.saleBruto) : '—'}</Td>
              <Td align="right" color={C.green}>{fmt(cb.creditoLiquido)}</Td>
              <Td align="right" color={C.red}>−{fmt(cb.debitadoChargeback)}</Td>
              <Td align="right" color={dBruto   > 0 ? C.red : C.green} bold>
                {dBruto > 0 ? `+${fmt(dBruto)}` : dBruto < 0 ? `−${fmt(Math.abs(dBruto))}` : '—'}
              </Td>
              <Td align="right" color={dLiquido > 0 ? C.red : C.green} bold>
                {dLiquido > 0 ? `+${fmt(dLiquido)}` : dLiquido < 0 ? `−${fmt(Math.abs(dLiquido))}` : '—'}
              </Td>
              <Td align="right" color={cb.custoOperacao > 0 ? C.red : C.muted}>
                {cb.custoOperacao > 0 ? `−${fmt(cb.custoOperacao)}` : '—'}
              </Td>
            </tr>
          );
        })}
      </Table>

      {/* Rodapé de totais */}
      <div style={{ padding: '12px 20px', borderTop: `2px solid ${C.border}`, display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 16 }}>
        <Stat label="Total Bruto Recebido"    value={fmt(totalBruto)}    accent={C.text}  size="sm" />
        <Stat label="Total Líquido Creditado" value={fmt(totalLiquido)}  accent={C.green} size="sm" />
        <Stat label="Total Debitado"          value={fmt(totalDebitado)} accent={C.red}   size="sm" />
        <Stat label="Δ vs Bruto"    value={(diffBruto  > 0 ? '+' : '') + fmt(diffBruto)}    accent={diffBruto  > 0 ? C.red : C.green} size="sm" sub="debitado − bruto" />
        <Stat label="Δ vs Líquido"  value={(diffLiquido > 0 ? '+' : '') + fmt(diffLiquido)} accent={diffLiquido > 0 ? C.red : C.green} size="sm" sub="debitado − líquido" />
        <Stat label="Prejuízo Total" value={fmt(r.totalPrejuizoChargebacks)} accent={C.red} size="lg" />
      </div>
    </Card>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
//  DRE corrigido — gap de antecipação + reordenação TED + alerta divergência  (NOVO)
// ─────────────────────────────────────────────────────────────────────────────

const DreSection = ({ r }: { r: FinanceReport }) => {
  // Valida apenas contra liquidoCreditEntries (vendas puras, sem CB)
  const calculado =
    r.brutoConciliated
    - r.feesTotal
    - r.taxTotal
    - r.adminTaxTotal
    - r.transactionTaxTotal
    - r.antecipationGapTotal;

  const discrepancia    = calculado - r.liquidoCreditEntries;
  const TOLERANCIA      = 1;
  const temDiscrepancia = Math.abs(discrepancia) > TOLERANCIA;

  // Partials acumulados — coluna direita mostra o saldo corrente após cada dedução
  const p2 = r.brutoConciliated - r.feesTotal;
  const p3 = p2 - r.taxTotal;
  const p4 = p3 - r.adminTaxTotal;
  const p5 = p4 - r.transactionTaxTotal;
  const p6 = p5 - r.antecipationGapTotal;

  // Partials continuam após o Líquido
  const liq = r.liquidoCreditEntries;
  const p7  = liq - r.chargebackDebitTotal;
  const p8  = p7  + r.chargebackRefundTotal;   // devolução é positivo
  const p9  = p8  - r.totalTaxasTED;
  const p10 = p9  - (r.totalSacado - r.totalTaxasTED);

  return (
    <Card>
      <CardHeader label="Demonstrativo de Resultado" />

      {temDiscrepancia && (
        <div style={{ margin: '12px 16px', padding: '10px 14px', background: C.amberBg, border: `1px solid #FCD34D`, borderRadius: 8, display: 'flex', gap: 10, alignItems: 'flex-start' }}>
          <span style={{ fontSize: 18, lineHeight: 1 }}>⚠️</span>
          <div>
            <div style={{ fontWeight: 700, fontSize: 13, color: C.amber }}>Diferença não explicada nas deduções</div>
            <div style={{ fontSize: 12, color: C.amber, marginTop: 3, lineHeight: 1.6 }}>
              A soma das deduções resulta em <strong>{fmt(calculado)}</strong>, mas o líquido real da API é <strong>{fmt(r.liquidoCreditEntries)}</strong>.{' '}
              Diferença: <strong>{discrepancia > 0 ? '+' : '−'}{fmt(Math.abs(discrepancia))}</strong>.
            </div>
          </div>
        </div>
      )}

      {/* Receita bruta e deduções de taxas */}
      <DreRow label="Bruto Total Conciliado"   value={r.brutoConciliated}    sign="+" />
      <DreRow label="Juros de Parcelamento"     value={r.feesTotal}           sign="−" indent color={C.red}   partial={p2} />
      <DreRow label="Taxa de Venda"             value={r.taxTotal}            sign="−" indent color={C.red}   partial={p3} />
      <DreRow label="Taxa Administrativa"       value={r.adminTaxTotal}       sign="−" indent color={C.red}   partial={p4} />
      <DreRow label="Taxa de Cartão de Crédito" value={r.transactionTaxTotal} sign="−" indent color={C.red}   partial={p5} />
      <DreRow
        label="Gap de Antecipação"
        value={-r.antecipationGapTotal}
        sign="+/−"
        indent
        color={r.antecipationGapTotal === 0 ? C.muted : r.antecipationGapTotal > 0 ? C.red : C.green}
        partial={p6}
      />

      {/* Líquido Final das vendas (sem impacto de chargebacks) */}
      {temDiscrepancia ? (
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '11px 20px', background: '#EEF2F8', borderTop: `2px solid #C8D3E8`, borderBottom: `1px solid ${C.border}` }}>
          <span style={{ fontWeight: 700, fontSize: 13, color: C.text, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            Líquido Final
            <span style={{ fontSize: 10, background: C.amberBg, color: C.amber, borderRadius: 999, padding: '2px 8px', fontWeight: 700, border: `1px solid #FCD34D` }}>✗ Diverge</span>
          </span>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 2 }}>
            <span style={{ fontWeight: 800, fontSize: 15, fontVariantNumeric: 'tabular-nums', color: C.green }}>{fmt(r.liquidoCreditEntries)}</span>
            <span style={{ fontSize: 11, color: C.amber, fontVariantNumeric: 'tabular-nums' }}>
              calculado: {fmt(calculado)}{' '}
              <span style={{ fontWeight: 700 }}>({discrepancia > 0 ? '+' : '−'}{fmt(Math.abs(discrepancia))})</span>
            </span>
          </div>
        </div>
      ) : (
        <DreRow label="Líquido Final" value={r.liquidoCreditEntries} subtotal />
      )}

      <div style={{ height: 1, background: C.border }} />

      {/* Chargebacks — agora abaixo do Líquido, na seção de fluxo de caixa */}
      <DreRow label="Chargebacks / Estornos (débito)"  value={r.chargebackDebitTotal}  sign="−" indent color={C.red}   partial={p7} />
      <DreRow label="Devolução de Taxas (marketplace)" value={r.chargebackRefundTotal} sign="+" indent color={C.green} partial={p8} />

      {/* Saques */}
      <DreRow label="Taxas de Saque (TED)" value={r.totalTaxasTED}                  sign="−" indent color={C.red}    partial={p9} />
      <DreRow label="Já Sacado (TEDs)"     value={r.totalSacado - r.totalTaxasTED}  sign="−" color={C.purple}         partial={p10} />

      <DreRow label="Saldo Atual" value={r.saldoAtual} highlight />
    </Card>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
//  Payload para análise por IA
// ─────────────────────────────────────────────────────────────────────────────

const buildAIPayload = (report: FinanceReport, financeData: FinanceItem[]): string => {
  return JSON.stringify({
    resumo: {
      totalVendasBruto:         report.totalSalesBruto,
      totalConciliado:          report.totalConciliated,
      totalNaoConciliado:       report.totalNotConciliated,
      brutoConciliated:         report.brutoConciliated,
      liquidoTotal:             report.liquidoTotal,
      totalSacado:              report.totalSacado,
      totalTaxasTED:            report.totalTaxasTED,
      saldoAtual:               report.saldoAtual,
      qtdChargebacks:           report.chargebacks.length,
      chargebackDebitTotal:     report.chargebackDebitTotal,
      chargebackRefundTotal:    report.chargebackRefundTotal,
      netChargebackImpact:      report.netChargebackImpact,
      custoOperacaoChargebacks: report.totalCustoOperacaoChargebacks,
      prejuizoChargebacks:      report.totalPrejuizoChargebacks,
      deducoes: {
        jurosParcelamento:  report.feesTotal,
        taxaVenda:          report.taxTotal,
        taxaAdministrativa: report.adminTaxTotal,
        taxaCartao:         report.transactionTaxTotal,
        gapAntecipacao:     report.antecipationGapTotal,
      },
    },
    chargebacks: report.chargebacks.map(cb => ({
      hash:               cb.chargebackEntry.hash,
      data:               cb.chargebackEntry.date,
      cliente:            cb.sale?.payer_id_response?.name ?? cb.sale?.client_id_response?.name ?? null,
      metodo:             cb.chargebackEntry.payment_method_id,
      saleBruto:          cb.saleBruto,
      creditoLiquido:     cb.creditoLiquido,
      debitadoChargeback: cb.debitadoChargeback,
      taxasDevolvidas:    cb.taxasDevolvidas,
      custoOperacao:      cb.custoOperacao,
      prejuizoTotal:      cb.prejuizoTotal,
      diffVsBruto:        cb.debitadoChargeback - cb.saleBruto,
      diffVsLiquido:      cb.debitadoChargeback - cb.creditoLiquido,
    })),
    naoConciliadas: report.salesNotConciliated.map(s => ({
      hash:         s.sale.sale_hash,
      cliente:      s.sale.payer_id_response?.name ?? s.sale.client_id_response?.name ?? null,
      metodo:       s.sale.payment_method_id,
      total:        s.sale.total,
      aprovadoEm:   s.sale.approved_at,
      disponivelEm: s.estimatedAvailableAt?.toISOString() ?? null,
      atrasada:     s.isPastDeadline,
    })),
    canceladasNoExtrato: report.salesCancelledInFinance.map(s => ({
      hash:    s.sale_hash,
      cliente: s.payer_id_response?.name ?? s.client_id_response?.name ?? null,
      status:  s.payment_status_id,
      total:   s.total,
    })),
    extrato: financeData.map(f => ({
      id:           f.item_id,
      hash:         f.hash,
      data:         f.date,
      tipo:         f.operation_type_raw,
      descricao:    f.operation_type,
      cliente:      f.client,
      bruto:        f.total,
      liquido:      f.net_total,
      saldo:        f.saldo,
      sacado:       f.sacado,
      taxaVenda:    f.tax,
      taxaAdmin:    f.administration_tax,
      taxaCartao:   f.transaction_tax,
      juros:        f.fees,
      gapAntecipacao: f.antecipation_gap,
      taxaTED:      f.operation_fee,
      metodo:       f.payment_method_id,
    })),
  }, null, 2);
};

const AI_SYSTEM_PROMPT = `Você é um auditor financeiro especialista em conciliação de pagamentos digitais para plataformas de educação online no Brasil.

Você receberá um JSON com dados financeiros extraídos de uma plataforma de vendas: resumo de totais, chargebacks detalhados, vendas não conciliadas, entradas canceladas no extrato e o extrato linha a linha.

Todos os valores monetários estão em CENTAVOS (divida por 100 para exibir em reais).

Sua tarefa:
1. Identificar divergências entre o bruto arrecadado e o que foi efetivamente creditado
2. Analisar se os chargebacks possuem custos anômalos (custo de operação alto, taxas não devolvidas)
3. Apontar vendas não conciliadas que estejam atrasadas e mereçam atenção
4. Verificar se a soma das deduções (taxas) é coerente com as taxas informadas
5. Identificar padrões suspeitos (ex: múltiplos chargebacks do mesmo cliente, valores exatos repetidos)
6. Verificar o gap de antecipação e se ele é esperado ou anômalo
7. Checar se o saldo final calculado bate com o saldo informado
8. Listar, em ordem de prioridade, as ações recomendadas ao gestor financeiro

Responda em português brasileiro, de forma objetiva e estruturada. Use bullet points e destaque os valores em reais (R$).`;

// ─────────────────────────────────────────────────────────────────────────────
//  Painel de Análise por IA  (NOVO)
// ─────────────────────────────────────────────────────────────────────────────

const AIAnalysisPanel = ({
  report,
  financeData,
  onClose,
}: {
  report: FinanceReport;
  financeData: FinanceItem[];
  onClose: () => void;
}) => {
  const [status, setStatus]     = useState<'idle' | 'loading' | 'done' | 'error'>('idle');
  const [analysis, setAnalysis] = useState('');

  const runAnalysis = async () => {
    setStatus('loading');
    setAnalysis('');
    try {
      const payload  = buildAIPayload(report, financeData);
      const response = await fetch('https://api.anthropic.com/v1/messages', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          model: 'claude-sonnet-4-20250514',
          max_tokens: 1000,
          system: AI_SYSTEM_PROMPT,
          messages: [{ role: 'user', content: `Analise os seguintes dados financeiros:\n\n${payload}` }],
        }),
      });
      const data = await response.json();
      const text = data?.content?.find((b: any) => b.type === 'text')?.text ?? 'Sem resposta.';
      setAnalysis(text);
      setStatus('done');
    } catch {
      setStatus('error');
    }
  };

  const downloadJSON = () => {
    const payload = buildAIPayload(report, financeData);
    const blob    = new Blob([payload], { type: 'application/json' });
    const url     = URL.createObjectURL(blob);
    const a       = document.createElement('a');
    a.href        = url;
    a.download    = `relatorio-ia-${new Date().toISOString().slice(0, 10)}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <>
      {/* Overlay */}
      <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.35)', zIndex: 999 }} />

      {/* Painel lateral */}
      <div style={{
        position: 'fixed', top: 0, right: 0, bottom: 0, width: 520, maxWidth: '95vw',
        background: C.surface, zIndex: 1000, display: 'flex', flexDirection: 'column',
        boxShadow: '-4px 0 32px rgba(30,58,95,0.15)', overflowY: 'auto',
      }}>
        {/* Header */}
        <div style={{ padding: '20px 24px 16px', borderBottom: `1px solid ${C.border}`, display: 'flex', alignItems: 'center', gap: 12, flexShrink: 0 }}>
          <span style={{ fontSize: 22 }}>🤖</span>
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 800, fontSize: 15, color: C.text }}>Análise por IA</div>
            <div style={{ fontSize: 12, color: C.muted }}>Arquivo de auditoria</div>
          </div>
          <button onClick={onClose} style={{ border: 'none', background: 'none', fontSize: 22, cursor: 'pointer', color: C.muted, lineHeight: 1 }}>×</button>
        </div>

        {/* Ações */}
        <div style={{ padding: '16px 24px', borderBottom: `1px solid ${C.border}`, display: 'flex', gap: 10, flexWrap: 'wrap', flexShrink: 0 }}>
          <button
            onClick={downloadJSON}
            style={{ flex: 1, padding: '10px 16px', borderRadius: 8, border: 'none', cursor: 'pointer', background: C.blue, color: '#fff', fontWeight: 700, fontSize: 13, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}
          >
            ⬇ Baixar arquivo de auditoria (JSON)
          </button>
        </div>

        {/* Mini-resumo sempre visível */}
        <div style={{ padding: '14px 24px', borderBottom: `1px solid ${C.border}`, background: C.bg, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px 16px', flexShrink: 0 }}>
          {[
            { label: 'Bruto Conciliado',  value: fmt(report.brutoConciliated) },
            { label: 'Líquido Final',     value: fmt(report.liquidoTotal) },
            { label: 'Chargebacks',       value: `${report.chargebacks.length} · ${fmt(report.chargebackDebitTotal)}` },
            { label: 'Não Conciliadas',   value: `${report.salesNotConciliated.length} · ${fmt(report.totalNotConciliated)}` },
            { label: 'Já Sacado',         value: fmt(report.totalSacado) },
            { label: 'Saldo Atual',       value: fmt(report.saldoAtual) },
          ].map(item => (
            <div key={item.label}>
              <div style={{ fontSize: 10, fontWeight: 700, color: C.muted, textTransform: 'uppercase', letterSpacing: '0.06em' }}>{item.label}</div>
              <div style={{ fontSize: 13, fontWeight: 700, color: C.text }}>{item.value}</div>
            </div>
          ))}
        </div>

        {/* Resultado da análise */}
        <div style={{ flex: 1, padding: '20px 24px', overflowY: 'auto' }}>
          {status === 'idle' && (
            <div style={{ color: C.muted, fontSize: 13, textAlign: 'center', marginTop: 40 }}>
              <div style={{ fontSize: 40, marginBottom: 12 }}>🧾</div>
              Clique em <strong>⬇ Baixar arquivo de auditoria</strong> para exportar o JSON completo e analisar manualmente ou em outra ferramenta de IA.
            </div>
          )}
          {status === 'error' && (
            <div style={{ color: C.red, fontWeight: 600, fontSize: 13 }}>
              ❌ Erro ao conectar com a IA. Tente novamente ou baixe o JSON para análise manual.
            </div>
          )}
          {(status === 'loading' || status === 'done') && (
            <div style={{ fontSize: 13, lineHeight: 1.8, color: C.text, whiteSpace: 'pre-wrap' }}>
              {analysis || ' '}
              {status === 'loading' && (
                <span style={{ display: 'inline-block', width: 8, height: 14, background: C.blue, marginLeft: 2, animation: 'blink 1s step-end infinite', verticalAlign: 'text-bottom' }} />
              )}
            </div>
          )}
        </div>
      </div>
    </>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
//  Aba: Relatório Geral
// ─────────────────────────────────────────────────────────────────────────────

// ─────────────────────────────────────────────────────────────────────────────
//  Tabela de composição do Bruto Conciliado
// ─────────────────────────────────────────────────────────────────────────────

const BrutoTable = ({ r }: { r: FinanceReport }) => {
  const canceladosBruto = r.brutoConciliated - r.totalSalesBruto;
  return (
    <Card>
      <CardHeader label="Composição do Bruto Conciliado" />
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
        <thead>
          <tr style={{ background: C.bg }}>
            {['Origem', 'Valor', 'Obs.'].map(h => (
              <th key={h} style={{ padding: '9px 16px', textAlign: h === 'Valor' ? 'right' : 'left', fontWeight: 700, fontSize: 11, color: C.muted, textTransform: 'uppercase', letterSpacing: '0.06em', borderBottom: `1px solid ${C.border}` }}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          <tr style={{ borderBottom: `1px solid ${C.border}` }}>
            <td style={{ padding: '10px 16px', fontWeight: 600, color: C.text }}>Bruto Total (Vendas Pagas)</td>
            <td style={{ padding: '10px 16px', textAlign: 'right', fontWeight: 700, color: C.blue, fontVariantNumeric: 'tabular-nums' }}>{fmt(r.totalSalesBruto)}</td>
            <td style={{ padding: '10px 16px', color: C.muted, fontSize: 12 }}>Σ payment_status=paid</td>
          </tr>
          <tr style={{ borderBottom: `1px solid ${C.border}` }}>
            <td style={{ padding: '10px 16px', fontWeight: 600, color: C.text }}>Divergência</td>
            <td style={{ padding: '10px 16px', textAlign: 'right', fontWeight: 700, color: canceladosBruto > 0 ? C.red : C.muted, fontVariantNumeric: 'tabular-nums' }}>
              {canceladosBruto > 0 ? `+${fmt(canceladosBruto)}` : fmt(canceladosBruto)}
            </td>
            <td style={{ padding: '10px 16px', color: C.muted, fontSize: 12 }}>Divergências</td>
          </tr>
          <tr style={{ background: C.blueLight }}>
            <td style={{ padding: '11px 16px', fontWeight: 800, color: C.blue }}>Bruto Total Conciliado</td>
            <td style={{ padding: '11px 16px', textAlign: 'right', fontWeight: 800, color: C.blue, fontSize: 15, fontVariantNumeric: 'tabular-nums' }}>{fmt(r.brutoConciliated)}</td>
            <td style={{ padding: '11px 16px', color: C.muted, fontSize: 12 }}>Σ operation_type_raw=credit</td>
          </tr>
        </tbody>
      </table>
    </Card>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
//  Seção de inconsistências de saldo (fim da capa)
// ─────────────────────────────────────────────────────────────────────────────

const SaldoInconsistenciesSection = ({ items }: { items: FinanceReport['saldoInconsistencies'] }) => {
  if (items.length === 0) return null;
  return (
    <Card>
      <CardHeader
        label={`${items.length} inconsistência${items.length !== 1 ? 's' : ''} de saldo detectada${items.length !== 1 ? 's' : ''}`}
        accent={C.red}
        right={
          <span style={{ fontSize: 11, background: C.redBg, color: C.red, borderRadius: 999, padding: '2px 10px', fontWeight: 700 }}>
            ⚠️ Verificação necessária
          </span>
        }
      />
      <div style={{ padding: '10px 20px 6px', fontSize: 12, color: C.muted, lineHeight: 1.6, borderBottom: `1px solid ${C.border}` }}>
        Para cada linha, o cálculo esperado é: <strong>saldo anterior + net_total = saldo</strong>. As linhas abaixo não obedecem essa relação (tolerância: 1 centavo).
      </div>
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12.5 }}>
          <thead>
            <tr>
              {['Data', 'Operação', 'Hash', 'Net Total', 'Saldo Anterior', 'Saldo Esperado', 'Saldo Real', 'Diferença'].map(h => (
                <th key={h} style={{ padding: '9px 14px', background: '#FEF2F2', color: C.red, fontWeight: 700, fontSize: 10.5, letterSpacing: '0.06em', textTransform: 'uppercase', textAlign: h === 'Data' || h === 'Operação' || h === 'Hash' ? 'left' : 'right', borderBottom: `2px solid #FECACA`, whiteSpace: 'nowrap' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {items.map((inc, i) => (
              <tr key={i} style={{ background: i % 2 === 0 ? '#FFF5F5' : '#fff', borderBottom: `1px solid #FEE2E2` }}>
                <Td nowrap muted>{fmtDateTime(inc.item.dateRef ?? inc.item.date)}</Td>
                <Td>{inc.item.operation_type || inc.item.operation_type_raw || '—'}</Td>
                <Td muted><code style={{ fontSize: 10, background: '#FEE2E2', padding: '1px 5px', borderRadius: 3 }}>{inc.item.hash || '—'}</code></Td>
                <Td align="right" color={inc.item.net_total >= 0 ? C.green : C.red} bold>
                  {inc.item.net_total >= 0 ? '+' : '−'}{fmt(Math.abs(inc.item.net_total))}
                </Td>
                <Td align="right" muted>{fmt(inc.prevSaldo)}</Td>
                <Td align="right" muted>{fmt(inc.expectedSaldo)}</Td>
                <Td align="right" bold>{fmt(inc.item.saldo ?? 0)}</Td>
                <Td align="right" color={inc.diff > 0 ? C.green : C.red} bold>
                  {inc.diff > 0 ? '+' : '−'}{fmt(Math.abs(inc.diff))}
                </Td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
//  Capa do relatório (Relatório Geral)
// ─────────────────────────────────────────────────────────────────────────────

const ReportOverview = ({
  r,
  finance,
  salesNotConciliated,
  onExportSaquePDF,
}: {
  r: FinanceReport;
  finance: FinanceItem[];
  salesNotConciliated: NotConciliatedSale[];
  onExportSaquePDF: (saqueIndex: number) => void;
}) => {
  const {configuration} = useConfiguration();
  const {open} = useTransferPopup();
  const [expandedSaque, setExpandedSaque] = useState<number | null>(null);

  // Reconstrói os períodos do linha a linha para o expand inline dos saques
  // (lógica espelhada da LinhaALinhaSection — mantém consistência visual)
  const allRows = [
    ...finance.map(f => ({
      kind: 'finance' as const,
      data: f,
      sortDate: new Date(f.dateRef ?? f.date).getTime(),
    })),
    ...salesNotConciliated
      .filter(s => !s.isPastDeadline)
      .map(s => ({
        kind: 'sale' as const,
        data: s,
        sortDate: s.estimatedAvailableAt?.getTime() ?? new Date(s.sale.approved_at ?? '').getTime(),
      })),
  ].sort((a, b) => a.sortDate - b.sortDate);

  let runningSaldo = 0;
  const rowsWithSaldo = allRows.map(row => {
    if (row.kind === 'finance') {
      const f = row.data as FinanceItem;
      if (f.operation_type_raw != null) runningSaldo = f.saldo ?? runningSaldo;
      return { ...row, saldoExibido: runningSaldo };
    } else {
      runningSaldo += (row.data as NotConciliatedSale).sale.net_total ?? 0;
      return { ...row, saldoExibido: runningSaldo };
    }
  });

  const periods: Array<{ rows: typeof rowsWithSaldo; hasSaque: boolean }> = [];
  let cur: typeof rowsWithSaldo = [];
  for (const row of rowsWithSaldo) {
    cur.push(row);
    if (row.kind === 'finance' && (row.data as FinanceItem).operation_type_raw === 'ted') {
      periods.push({ rows: cur, hasSaque: true }); cur = [];
    }
  }
  if (cur.length > 0) periods.push({ rows: cur, hasSaque: false });

  const ESTORNO_DEBITO  = new Set(['chargeback', 'refund']);
  const ESTORNO_CREDITO = new Set(['chargeback_refund']);

  const rowColor = (f: FinanceItem) => {
    if (f.operation_type_raw === 'ted')              return '#F0FDF4';
    if (ESTORNO_DEBITO.has(f.operation_type_raw!))   return '#FFF1F2';
    if (ESTORNO_CREDITO.has(f.operation_type_raw!))  return '#FFF7ED';
    if (f.operation_type_raw === 'internal_transfer') return '#F8FAFC';
    return 'transparent';
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      <ReconciliationBanner r={r} />

      {/* Cards de alto nível */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12 }}>
        {[
          { label: 'Bruto Total (Vendas Pagas)', value: fmt(r.totalSalesBruto),    accent: C.blue },
          { label: 'Conciliado',                 value: fmt(r.totalConciliated),   accent: C.green,  sub: `${r.salesConciliated.length} vendas` },
          { label: 'Não Conciliado',             value: fmt(r.totalNotConciliated),accent: r.totalNotConciliated > 0 ? C.red : C.muted, sub: `${r.salesNotConciliated.length} vendas` },
          { label: 'Líquido Final',              value: fmt(r.liquidoTotal),        accent: C.green },
          { label: 'Já Sacado',                  value: fmt(r.totalSacado),         accent: C.purple, sub: `${r.saqueEntries.length} saques` },
          { label: 'Saldo Disponível',           value: fmt(r.saldoAtual),          accent: r.saldoAtual < 0 ? C.red : C.purple },
        ].map(c => (
          <Card key={c.label} style={{ padding: 16, borderTop: `3px solid ${c.accent}` }}>
            <Stat label={c.label} value={c.value} sub={(c as any).sub} accent={c.accent} size="md" />
          </Card>
        ))}
      </div>

      <BrutoTable r={r} />
      <DreSection r={r} />

      {/* ── Saques Realizados ────────────────────────────────────────────── */}
      {r.saqueEntries.length > 0 && (
        <Card>
          <CardHeader
            label={`${r.saqueEntries.length} Saque${r.saqueEntries.length !== 1 ? 's' : ''} Realizado${r.saqueEntries.length !== 1 ? 's' : ''}`}
            accent={C.green}
            right={
              <span style={{ fontSize: 12, color: C.muted }}>
                Total sacado: <strong style={{ color: C.purple }}>{fmt(r.totalSacado)}</strong>
              </span>
            }
          />
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12.5 }}>
              <thead>
                <tr>
                  {['#', 'Data do Saque', 'Bruto do Período', 'Taxa TED', 'Líquido Sacado', 'Saldo Após', 'Linha a Linha', 'PDF'].map(h => (
                    <th key={h} style={{ padding: '9px 14px', background: '#F1F5FB', color: C.muted, fontWeight: 700, fontSize: 10.5, letterSpacing: '0.06em', textTransform: 'uppercase', textAlign: ['Bruto do Período', 'Taxa TED', 'Líquido Sacado', 'Saldo Após'].includes(h) ? 'right' : 'left', borderBottom: `1px solid ${C.border}`, whiteSpace: 'nowrap' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {r.saqueEntries.map((saque, si) => {
                  const period   = periods[si];            // período correspondente (índice alinha com saques)
                  const isOpen   = expandedSaque === si;
                  const credits  = period?.rows.filter(r => r.kind === 'finance' && (r.data as FinanceItem).operation_type_raw === 'credit') ?? [];
                  const brutoPeriodo = credits.reduce((a, r) => a + ((r.data as FinanceItem).total ?? 0), 0);
                  const saldoApos    = saque.saldo ?? 0;

                  return (
                    <React.Fragment key={saque.item_id}>
                      <tr style={{ background: isOpen ? C.blueLight : 'transparent', borderBottom: `1px solid ${C.border}` }}>
                        <Td muted bold>{si + 1}</Td>
                        <Td nowrap bold>{fmtDateTime(saque.dateRef ?? saque.date)}</Td>
                        <Td align="right">{fmt(brutoPeriodo)}</Td>
                        <Td align="right" color={C.red}>{saque.operation_fee > 0 ? `−${fmt(saque.operation_fee)}` : '—'}</Td>
                        <Td align="right" color={C.green} bold>{fmt(Math.abs(saque.net_total ?? 0))}</Td>
                        <Td align="right" color={saldoApos < 0 ? C.red : C.muted}>{fmt(saldoApos)}</Td>
                        <Td>
                          <button
                            onClick={() => setExpandedSaque(isOpen ? null : si)}
                            style={{ display: 'flex', alignItems: 'center', gap: 5, border: 'none', background: isOpen ? C.blue : C.blueLight, color: isOpen ? '#fff' : C.blue, fontWeight: 700, fontSize: 11, borderRadius: 6, padding: '4px 10px', cursor: 'pointer' }}
                          >
                            {isOpen ? '▲ Fechar' : '▼ Ver linhas'}
                          </button>
                        </Td>
                        <Td>
                          <button
                            onClick={() => onExportSaquePDF(si + 1)}
                            style={{ display: 'flex', alignItems: 'center', gap: 5, border: 'none', background: C.redBg, color: C.red, fontWeight: 700, fontSize: 11, borderRadius: 6, padding: '4px 10px', cursor: 'pointer' }}
                          >
                            <FaFilePdf size={12} /> PDF
                          </button>
                        </Td>

  <Td>
                             <button onClick={() => open(`${configuration?.url}`)} title="Exportar Excel"
                                    style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '7px 14px', borderRadius: 8, border: `1px solid #E3E8F0`, background: '#fff', color: '#0A7A55', fontWeight: 600, fontSize: 12, cursor: 'pointer' }}>
                                    <FaWallet size={14} /> Transferências
                                  </button>
                                  </Td>
                      </tr>

                      {/* ── Expand inline: linha a linha do período ── */}
                      {isOpen && period && (
                        <tr>
                          <td colSpan={8} style={{ padding: 0, borderBottom: `2px solid ${C.border}` }}>
                            <div style={{ background: C.blueLight, padding: '0 0 4px 0' }}>
                              <div style={{ overflowX: 'auto' }}>
                                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                                  <thead>
                                    <tr style={{ background: C.blueLight }}>
                                      {['Aprovação', 'Conciliação', 'Operação', 'Cliente', 'Método', 'Bruto', 'Líquido', 'Saldo', 'Situação'].map(h => (
                                        <th key={h} style={{ padding: '7px 12px', color: C.blue, fontWeight: 700, fontSize: 10, letterSpacing: '0.06em', textTransform: 'uppercase', textAlign: ['Bruto', 'Líquido', 'Saldo'].includes(h) ? 'right' : 'left', borderBottom: `1px solid #C8D3E8`, whiteSpace: 'nowrap' }}>{h}</th>
                                      ))}
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {period.rows.map((row, ri) => {
                                      if (row.kind === 'sale') {
                                        const { sale } = row.data as NotConciliatedSale;
                                        return (
                                          <tr key={`sale-${ri}`} style={{ background: '#F5F3FF', borderBottom: `1px solid #E0E7FF` }}>
                                            <td style={{ padding: '7px 12px', color: C.muted, fontSize: 12, whiteSpace: 'nowrap' }}>{fmtDateTime(sale.approved_at)}</td>
                                            <td style={{ padding: '7px 12px', color: '#6366F1', fontSize: 11 }}>estimado</td>
                                            <td style={{ padding: '7px 12px', color: '#6366F1', fontWeight: 700 }}>🕐 Ag. Conciliação</td>
                                            <td style={{ padding: '7px 12px', fontWeight: 600 }}>{sale.payer_id_response?.name ?? sale.client_id_response?.name ?? '—'}</td>
                                            <td style={{ padding: '7px 12px', color: C.muted }}>{sale.payment_method_id}</td>
                                            <td style={{ padding: '7px 12px', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{fmt(sale.total)}</td>
                                            <td style={{ padding: '7px 12px', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{fmt(sale.net_total)}</td>
                                            <td style={{ padding: '7px 12px', textAlign: 'right', color: '#6366F1', fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>{fmt(row.saldoExibido)}</td>
                                            <td style={{ padding: '7px 12px' }}><Pill label="Aguardando" color="#5B21B6" bg="#EDE9FE" /></td>
                                          </tr>
                                        );
                                      }
                                      const item = row.data as FinanceItem;
                                      const isTed = item.operation_type_raw === 'ted';
                                      const bg    = rowColor(item);
                                      return (
                                        <tr key={`fi-${item.item_id}-${ri}`} style={{ background: bg, borderBottom: `1px solid ${isTed ? C.border : '#EEF2F8'}`, fontWeight: isTed ? 700 : undefined }}>
                                          <td style={{ padding: '7px 12px', color: C.muted, fontSize: 12, whiteSpace: 'nowrap' }}>{fmtDateTime(item.dateRef ?? item.date)}</td>
                                          <td style={{ padding: '7px 12px', color: C.muted, fontSize: 12, whiteSpace: 'nowrap' }}>{fmtDate(item.date)}</td>
                                          <td style={{ padding: '7px 12px', color: isTed ? C.green : C.text, fontWeight: isTed ? 800 : undefined }}>{item.operation_type || item.operation_type_raw || '—'}</td>
                                          <td style={{ padding: '7px 12px' }}>{item.client || '—'}</td>
                                          <td style={{ padding: '7px 12px', color: C.muted }}>{item.payment_method_id || '—'}</td>
                                          <td style={{ padding: '7px 12px', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{item.total != null ? fmt(item.total) : '—'}</td>
                                          <td style={{ padding: '7px 12px', textAlign: 'right', fontVariantNumeric: 'tabular-nums', color: item.net_total >= 0 ? C.green : C.red }}>{item.net_total != null ? fmt(item.net_total) : '—'}</td>
                                          <td style={{ padding: '7px 12px', textAlign: 'right', fontVariantNumeric: 'tabular-nums', fontWeight: 700 }}>{fmt(row.saldoExibido)}</td>
                                          <td style={{ padding: '7px 12px' }}>
                                            {isTed && <Pill label="Saque (TED)" color={C.green} bg={C.greenBg} />}
                                            {ESTORNO_DEBITO.has(item.operation_type_raw!) && <Pill label="Chargeback" color={C.red} bg={C.redBg} />}
                                            {ESTORNO_CREDITO.has(item.operation_type_raw!) && <Pill label="Estorno CB" color={C.amber} bg={C.amberBg} />}
                                          </td>
                                        </tr>
                                      );
                                    })}
                                  </tbody>
                                </table>
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {/* Alertas */}
      {r.chargebacks.length > 0 && (
        <div style={{ background: C.redBg, border: `1px solid #FECACA`, borderRadius: 10, padding: '12px 16px', display: 'flex', gap: 10 }}>
          <span style={{ fontSize: 18 }}>⚠️</span>
          <div>
            <div style={{ fontWeight: 700, color: C.red, fontSize: 13 }}>
              {r.chargebacks.length} chargeback{r.chargebacks.length !== 1 ? 's' : ''} · custo total de operação: {fmt(r.totalCustoOperacaoChargebacks)}
            </div>
            <div style={{ color: C.red, fontSize: 12, marginTop: 2 }}>Veja a aba "Divergências" para o detalhamento.</div>
          </div>
        </div>
      )}
      {r.salesNotConciliated.filter(s => s.isPastDeadline).length > 0 && (
        <div style={{ background: C.amberBg, border: `1px solid #FCD34D`, borderRadius: 10, padding: '12px 16px', display: 'flex', gap: 10 }}>
          <span style={{ fontSize: 18 }}>🔍</span>
          <div>
            <div style={{ fontWeight: 700, color: C.amber, fontSize: 13 }}>
              {r.salesNotConciliated.filter(s => s.isPastDeadline).length} venda(s) pagas fora do prazo sem conciliação
            </div>
            <div style={{ color: C.amber, fontSize: 12, marginTop: 2 }}>
              Verifique se foram liberadas manualmente ou perderam o relacionamento com o meio de pagamento.
            </div>
          </div>
        </div>
      )}

      {/* Inconsistências de saldo — sempre ao final da capa */}
      <SaldoInconsistenciesSection items={r.saldoInconsistencies} />
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
//  Aba: Não Conciliadas
// ─────────────────────────────────────────────────────────────────────────────

const NotConciliatedSection = ({ items }: { items: NotConciliatedSale[] }) => {
  const atrasadas  = items.filter(i => i.isPastDeadline);
  const aguardando = items.filter(i => !i.isPastDeadline);

  if (items.length === 0) {
    return <p style={{ color: C.green, fontWeight: 600, padding: 4 }}>Todas as vendas pagas estão conciliadas. ✅</p>;
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      {atrasadas.length > 0 && (
        <Card>
          <CardHeader label={`${atrasadas.length} venda(s) fora do prazo — verificação necessária`} accent={C.red} />
          <Table headers={['Criação', 'Aprovação', 'Cliente', 'Método', 'Bruto', 'Transaction ID', 'Sale Hash', 'Prazo estimado', 'Situação']}>
            {atrasadas.map(({ sale, estimatedAvailableAt }) => (
              <tr key={sale._id}>
                <Td nowrap muted>{fmtDate(sale.createdAt)}</Td>
                <Td nowrap muted>{fmtDateTime(sale.approved_at)}</Td>
                <Td bold>{sale.payer_id_response?.name ?? sale.client_id_response?.name ?? '—'}</Td>
                <Td>{sale.payment_method_id}</Td>
                <Td align="right">{fmt(sale.total)}</Td>
                <Td muted><code style={{ fontSize: 11 }}>{sale.transaction_id ?? '—'}</code></Td>
                <Td muted><code style={{ fontSize: 11 }}>{sale.sale_hash || '—'}</code></Td>
                <Td nowrap>{estimatedAvailableAt ? fmtDate(estimatedAvailableAt.toISOString()) : '—'}</Td>
                <Td><Pill label="Atrasada" color={C.red} bg={C.redBg} /></Td>
              </tr>
            ))}
          </Table>
          <div style={{ padding: '10px 14px', borderTop: `1px solid ${C.border}`, display: 'flex', justifyContent: 'flex-end', gap: 6 }}>
            <span style={{ fontSize: 12, color: C.muted }}>Total:</span>
            <strong style={{ color: C.red }}>{fmt(atrasadas.reduce((a, i) => a + i.sale.total, 0))}</strong>
          </div>
        </Card>
      )}

      {aguardando.length > 0 && (
        <Card>
          <CardHeader label={`${aguardando.length} venda(s) dentro do prazo de compensação`} accent={C.amber} />
          <Table headers={['Criação', 'Aprovação', 'Cliente', 'Método', 'Bruto', 'Transaction ID', 'Sale Hash', 'Disponível em']}>
            {aguardando.map(({ sale, estimatedAvailableAt }) => (
              <tr key={sale._id}>
                <Td nowrap muted>{fmtDate(sale.createdAt)}</Td>
                <Td nowrap muted>{fmtDateTime(sale.approved_at)}</Td>
                <Td bold>{sale.payer_id_response?.name ?? sale.client_id_response?.name ?? '—'}</Td>
                <Td>{sale.payment_method_id}</Td>
                <Td align="right">{fmt(sale.total)}</Td>
                <Td muted><code style={{ fontSize: 11 }}>{sale.transaction_id ?? '—'}</code></Td>
                <Td muted><code style={{ fontSize: 11 }}>{sale.sale_hash || '—'}</code></Td>
                <Td nowrap>{estimatedAvailableAt ? fmtDate(estimatedAvailableAt.toISOString()) : '—'}</Td>
              </tr>
            ))}
          </Table>
        </Card>
      )}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
//  Drawer: Histórico de compras do cliente
// ─────────────────────────────────────────────────────────────────────────────

const UserSalesDrawer = ({
  clientId,
  clientName,
  currentProjectUrl,
  onClose,
}: {
  clientId: string | number;
  clientName: string;
  currentProjectUrl: string;
  onClose: () => void;
}) => {
  const [sales, setSales]       = useState<any[]>([]);
  const [projectMap, setProjectMap] = useState<Map<string, string>>(new Map());
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [salesRes, projectsRes] = await Promise.all([
          api.get('/sales', {
            params: { where: { '$or': [{ payer_id: clientId }, { client_id: clientId }] } },
          }),
          api.get('/products', { params: { limitItems: 1000 } }),
        ]);
        const rows: any[]    = salesRes?.data?.rows ?? [];
        const projects: any[] = projectsRes?.data?.rows ?? [];
        // Mapear project.url → project.title
        const map = new Map<string, string>();
        for (const p of projects) {
          if (p.url) map.set(p.url, p.title ?? p.url);
        }
        setSales(rows);
        setProjectMap(map);
      } catch {
        setError(true);
      } finally {
        setLoading(false);
      }
    })();
  }, [clientId]);

  const thisProject  = sales.filter((s: any) => s.project_id === currentProjectUrl);
  const otherProject = sales.filter((s: any) => s.project_id !== currentProjectUrl);

  const totalBruto  = sales.reduce((a: number, s: any) => a + (s.total ?? 0), 0);
  const countPaid   = sales.filter((s: any) => s.payment_status_id === 'paid').length;
  const countCancel = sales.filter((s: any) => s.payment_status_id === 'cancelled').length;

  const statusColor: Record<string, string> = { paid: C.green, cancelled: C.red, waiting: C.amber };
  const statusLabel: Record<string, string> = { paid: 'Pago', cancelled: 'Cancelado', waiting: 'Aguardando' };

  const SalesTable = ({ rows, showProject }: { rows: any[]; showProject: boolean }) => {
    const headers = ['Data', 'Produto', ...(showProject ? ['Projeto'] : []), 'Método', 'Bruto', 'Status'];
    return (
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12.5 }}>
        <thead>
          <tr>
            {headers.map(h => (
              <th key={h} style={{ padding: '8px 14px', background: '#F1F5FB', color: C.muted, fontWeight: 700, fontSize: 10.5, letterSpacing: '0.06em', textTransform: 'uppercase', textAlign: h === 'Bruto' ? 'right' : 'left', borderBottom: `1px solid ${C.border}`, whiteSpace: 'nowrap', position: 'sticky', top: 0, zIndex: 1 }}>
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((s: any, i: number) => (
            <tr key={i} style={{ borderBottom: `1px solid #F0F2F7` }}>
              <td style={{ padding: '8px 14px', color: C.muted, whiteSpace: 'nowrap' }}>{fmtDate(s.approved_at ?? s.createdAt)}</td>
              <td style={{ padding: '8px 14px', color: C.text, maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {s.product_id_response?.title ?? s.product_id ?? '—'}
              </td>
              {showProject && (
                <td style={{ padding: '8px 14px', color: C.muted, maxWidth: 140, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {projectMap.get(s.project_id) ?? s.project_id ?? '—'}
                </td>
              )}
              <td style={{ padding: '8px 14px', color: C.muted }}>{s.payment_method_id ?? '—'}</td>
              <td style={{ padding: '8px 14px', textAlign: 'right', fontVariantNumeric: 'tabular-nums', fontWeight: 600, color: 'rgb(26, 34, 54)' }}>{fmt(s.total ?? 0)}</td>
              <td style={{ padding: '8px 14px' }}>
                <span style={{ display: 'inline-block', padding: '2px 9px', borderRadius: 999, fontSize: 10.5, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em', color: statusColor[s.payment_status_id] ?? C.muted, background: s.payment_status_id === 'paid' ? C.greenBg : s.payment_status_id === 'cancelled' ? C.redBg : C.amberBg }}>
                  {statusLabel[s.payment_status_id] ?? s.payment_status_id ?? '—'}
                </span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    );
  };

  return (
    <>
      <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.35)', zIndex: 999 }} />
      <div style={{ position: 'fixed', top: 0, right: 0, bottom: 0, width: 640, maxWidth: '95vw', background: C.surface, zIndex: 1000, display: 'flex', flexDirection: 'column', boxShadow: '-4px 0 32px rgba(30,58,95,0.15)' }}>

        {/* Header */}
        <div style={{ padding: '20px 24px 16px', borderBottom: `1px solid ${C.border}`, display: 'flex', alignItems: 'center', gap: 12, flexShrink: 0 }}>
          <span style={{ fontSize: 22 }}>👤</span>
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 800, fontSize: 15, color: C.text }}>{clientName || 'Cliente'}</div>
            <div style={{ fontSize: 12, color: C.muted }}>Histórico completo de compras</div>
          </div>
          <button onClick={onClose} style={{ border: 'none', background: 'none', fontSize: 22, cursor: 'pointer', color: C.muted, lineHeight: 1 }}>×</button>
        </div>

        {/* KPIs */}
        {!loading && !error && (
          <div style={{ padding: '14px 24px', borderBottom: `1px solid ${C.border}`, background: C.bg, display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '8px 16px', flexShrink: 0 }}>
            {[
              { label: 'Total de compras', value: String(sales.length) },
              { label: 'Pagas',            value: String(countPaid) },
              { label: 'Canceladas',       value: String(countCancel) },
              { label: 'Bruto total',      value: fmt(totalBruto) },
            ].map(item => (
              <div key={item.label}>
                <div style={{ fontSize: 10, fontWeight: 700, color: C.muted, textTransform: 'uppercase', letterSpacing: '0.06em' }}>{item.label}</div>
                <div style={{ fontSize: 14, fontWeight: 700, color: C.text }}>{item.value}</div>
              </div>
            ))}
          </div>
        )}

        {/* Corpo */}
        <div style={{ flex: 1, overflowY: 'auto' }}>
          {loading && (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 160, gap: 10, color: C.muted, fontSize: 13 }}>
              <div style={{ width: 18, height: 18, borderRadius: '50%', border: `2px solid ${C.border}`, borderTopColor: C.blue, animation: 'spin 0.8s linear infinite' }} />
              Buscando compras…
            </div>
          )}
          {error && (
            <div style={{ padding: 24, color: C.red, fontWeight: 600, fontSize: 13 }}>❌ Erro ao buscar o histórico do cliente. Verifique a conexão.</div>
          )}
          {!loading && !error && sales.length === 0 && (
            <div style={{ padding: 24, color: C.muted, fontSize: 13 }}>Nenhuma compra encontrada para este cliente.</div>
          )}
          {!loading && !error && sales.length > 0 && (
            <div style={{ display: 'flex', flexDirection: 'column' }}>

              {/* Tabela: Este projeto */}
              <div style={{ padding: '14px 20px 6px', display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ fontWeight: 700, fontSize: 12, color: C.text }}>Este projeto</span>
                <span style={{ background: C.blueLight, color: C.blue, fontWeight: 700, fontSize: 11, padding: '1px 8px', borderRadius: 999 }}>{thisProject.length}</span>
              </div>
              {thisProject.length === 0
                ? <p style={{ padding: '6px 20px 14px', color: C.muted, fontSize: 12, margin: 0 }}>Nenhuma compra neste projeto.</p>
                : <SalesTable rows={thisProject} showProject={false} />
              }

              {/* Tabela: Outros projetos — só exibe se houver */}
              {otherProject.length > 0 && (
                <>
                  <div style={{ padding: '18px 20px 6px', display: 'flex', alignItems: 'center', gap: 8, borderTop: `2px solid ${C.border}`, marginTop: 4 }}>
                    <span style={{ fontWeight: 700, fontSize: 12, color: C.text }}>Outros projetos</span>
                    <span style={{ background: C.amberBg, color: C.amber, fontWeight: 700, fontSize: 11, padding: '1px 8px', borderRadius: 999 }}>{otherProject.length}</span>
                  </div>
                  <SalesTable rows={otherProject} showProject={true} />
                </>
              )}

            </div>
          )}
        </div>
      </div>
    </>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
//  Aba: Divergências
// ─────────────────────────────────────────────────────────────────────────────

const DivergenceSection = ({
  chargebacks,
  creditosOrfaos,
  onViewUserSales,
}: {
  chargebacks: ChargebackMatch[];
  creditosOrfaos: FinanceItem[];
  onViewUserSales: (clientId: string | number, clientName: string) => void;
}) => (
  <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>

    {/* ── Linha a Linha sem venda em sales — só exibe quando há ocorrências ── */}
    {creditosOrfaos.length > 0 && (
      <Card>
        <CardHeader
          label={`${creditosOrfaos.length} entrada${creditosOrfaos.length !== 1 ? 's' : ''} no extrato sem venda correspondente`}
          accent={C.purple}
          right={
            <span style={{ fontSize: 12, color: C.muted }}>
              Total:&nbsp;<strong style={{ color: C.purple }}>{fmt(creditosOrfaos.reduce((a, f) => a + (f.total ?? 0), 0))}</strong>
            </span>
          }
        />
        <Table headers={['Data', 'Cliente', 'Hash', 'Método', 'Bruto', 'Líquido', 'Ver compras']}>
          {creditosOrfaos.map((f, i) => (
            <tr key={i} style={{ background: C.purpleBg }}>
              <Td nowrap muted>{fmtDate(f.date)}</Td>
              <Td bold>{f.client || '—'}</Td>
              <Td muted><code style={{ fontSize: 11 }}>{f.hash}</code></Td>
              <Td muted nowrap>{f.payment_method_id || '—'}</Td>
              <Td align="right">{fmt(f.total ?? 0)}</Td>
              <Td align="right" color={C.green}>{fmt(f.net_total)}</Td>
              <Td>
                {f.client_id && (
                  <button
                    onClick={() => onViewUserSales(f.client_id!, f.client || 'Cliente')}
                    style={{ border: 'none', background: C.purpleBg, color: C.purple, cursor: 'pointer', fontWeight: 700, fontSize: 12, borderRadius: 6, padding: '3px 10px', display: 'flex', alignItems: 'center', gap: 4 }}
                  >
                    👤 Ver compras
                  </button>
                )}
              </Td>
            </tr>
          ))}
        </Table>
      </Card>
    )}

    {/* ── Chargebacks / Estornos (modelo original + Ver compras) ── */}
    <Card>
      <CardHeader
        label={`${chargebacks.length} Chargeback${chargebacks.length !== 1 ? 's' : ''} / Estorno${chargebacks.length !== 1 ? 's' : ''}`}
        accent={C.red}
        right={
          chargebacks.length > 0
            ? <span style={{ fontSize: 12, color: C.muted }}>
                Custo total:&nbsp;
                <strong style={{ color: C.red }}>{fmt(chargebacks.reduce((a, c) => a + Math.max(0, c.custoOperacao), 0))}</strong>
              </span>
            : undefined
        }
      />
      {chargebacks.length === 0
        ? <p style={{ padding: '16px 20px', color: C.green, fontWeight: 600, margin: 0 }}>Nenhum chargeback ou estorno identificado. ✅</p>
        : (
          <>
            {(() => {
              const sumBruto    = chargebacks.reduce((a,c) => a + c.saleBruto, 0);
              const sumLiquido  = chargebacks.reduce((a,c) => a + c.creditoLiquido, 0);
              const sumEstorno  = chargebacks.reduce((a,c) => a + c.debitadoChargeback, 0);
              const sumDvsBruto = chargebacks.reduce((a,c) => a + (c.debitadoChargeback - c.saleBruto), 0);
              const sumDvsLiq   = chargebacks.reduce((a,c) => a + (c.debitadoChargeback - c.creditoLiquido), 0);
              const sumCusto    = chargebacks.reduce((a,c) => a + Math.max(0, c.custoOperacao), 0);
              const tfootTd = (v: number, pos?: boolean, neg?: boolean) => {
                const color = pos ? C.red : neg ? C.green : C.text;
                return (
                  <td style={{ padding: '9px 14px', fontWeight: 800, fontSize: 12.5, textAlign: 'right', fontVariantNumeric: 'tabular-nums', color, background: '#FEF2F2', borderTop: `2px solid #FECACA`, whiteSpace: 'nowrap' }}>
                    {v === 0 ? '—' : pos ? `+${fmt(Math.abs(v))}` : neg ? `−${fmt(Math.abs(v))}` : fmt(Math.abs(v))}
                  </td>
                );
              };
              return (
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12.5 }}>
                    <thead>
                      <tr>
                        {['Data', 'Cliente', 'Hash', 'Método', 'Bruto', 'Líquido Creditado', 'Estorno', 'Δ vs Bruto', 'Δ vs Líquido', 'Custo Op.', 'Ver compras'].map((h, i) => (
                          <th key={i} style={{ padding: '9px 14px', background: '#F1F5FB', color: C.muted, fontWeight: 700, fontSize: 10.5, letterSpacing: '0.06em', textTransform: 'uppercase', textAlign: 'left', borderBottom: `1px solid ${C.border}`, whiteSpace: 'nowrap' }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {chargebacks.map((cb, i) => {
                        const cliente  = cb.sale?.payer_id_response?.name ?? cb.sale?.client_id_response?.name ?? '—';
                        const dBruto   = cb.debitadoChargeback - cb.saleBruto;
                        const dLiquido = cb.debitadoChargeback - cb.creditoLiquido;
                        const cid      = (cb.chargebackEntry as any).client_id
                                       ?? (cb.sale as any)?.client_id
                                       ?? (cb.sale as any)?.payer_id;
                        return (
                          <tr key={i} style={{ background: '#FFF5F5' }}>
                            <Td nowrap muted>{fmtDate(cb.chargebackEntry.date)}</Td>
                            <Td bold>{cliente}</Td>
                            <Td muted><code style={{ fontSize: 11 }}>{cb.chargebackEntry.hash}</code></Td>
                            <Td muted nowrap>{cb.chargebackEntry.payment_method_id || cb.sale?.payment_method_id || '—'}</Td>
                            <Td align="right">{cb.saleBruto > 0 ? fmt(cb.saleBruto) : '—'}</Td>
                            <Td align="right" color={C.green}>{fmt(cb.creditoLiquido)}</Td>
                            <Td align="right" color={C.red}>−{fmt(cb.debitadoChargeback)}</Td>
                            <Td align="right" color={dBruto   > 0 ? C.red : C.green} bold>
                              {dBruto > 0 ? `+${fmt(dBruto)}` : dBruto < 0 ? `−${fmt(Math.abs(dBruto))}` : '—'}
                            </Td>
                            <Td align="right" color={dLiquido > 0 ? C.red : C.green} bold>
                              {dLiquido > 0 ? `+${fmt(dLiquido)}` : dLiquido < 0 ? `−${fmt(Math.abs(dLiquido))}` : '—'}
                            </Td>
                            <Td align="right" color={cb.custoOperacao > 0 ? C.red : C.muted}>
                              {cb.custoOperacao > 0 ? `−${fmt(cb.custoOperacao)}` : '—'}
                            </Td>
                            <Td>
                              {cid && (
                                <button
                                  onClick={() => onViewUserSales(cid, cliente)}
                                  style={{ border: 'none', background: '#FFF5F5', color: C.red, cursor: 'pointer', fontWeight: 700, fontSize: 12, borderRadius: 6, padding: '3px 10px', display: 'flex', alignItems: 'center', gap: 4 }}
                                >
                                  👤 Ver compras
                                </button>
                              )}
                            </Td>
                          </tr>
                        );
                      })}
                    </tbody>
                    <tfoot>
                      <tr>
                        <td colSpan={4} style={{ padding: '9px 14px', fontWeight: 800, fontSize: 11, color: C.muted, background: '#FEF2F2', borderTop: `2px solid #FECACA`, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                          Totais ({chargebacks.length})
                        </td>
                        {tfootTd(sumBruto)}
                        {tfootTd(sumLiquido)}
                        <td style={{ padding: '9px 14px', fontWeight: 800, fontSize: 12.5, textAlign: 'right', fontVariantNumeric: 'tabular-nums', color: C.red, background: '#FEF2F2', borderTop: `2px solid #FECACA` }}>
                          −{fmt(sumEstorno)}
                        </td>
                        {tfootTd(sumDvsBruto, sumDvsBruto > 0, sumDvsBruto < 0)}
                        {tfootTd(sumDvsLiq,   sumDvsLiq   > 0, sumDvsLiq   < 0)}
                        <td style={{ padding: '9px 14px', fontWeight: 800, fontSize: 12.5, textAlign: 'right', fontVariantNumeric: 'tabular-nums', color: C.red, background: '#FEF2F2', borderTop: `2px solid #FECACA` }}>
                          {sumCusto > 0 ? `−${fmt(sumCusto)}` : '—'}
                        </td>
                        <td style={{ background: '#FEF2F2', borderTop: `2px solid #FECACA` }} />
                      </tr>
                    </tfoot>
                  </table>
                </div>
              );
            })()}

          </>
        )
      }
    </Card>

  </div>
);

// ─────────────────────────────────────────────────────────────────────────────
//  Aba: Linha a Linha  — com PDF por saque e colunas de data separadas
// ─────────────────────────────────────────────────────────────────────────────

// ─────────────────────────────────────────────────────────────────────────────
//  Aba: Linha a Linha  — accordion saque a saque + resumo por período
// ─────────────────────────────────────────────────────────────────────────────

const LegendaDot = ({ color, label }: { color: string; label: string }) => (
  <span style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 11, color: '#000' }}>
    <span style={{ width: 10, height: 10, borderRadius: 2, background: color, border: `1.5px solid ${color}`, display: 'inline-block', opacity: 0.7 }} />
    {label}
  </span>
);

// Resumo de um período (entre saques)
interface PeriodSummary {
  bruto: number;
  sobrantesInicio: number;   // saldo antes do primeiro entry do período
  juros: number;
  taxaVenda: number;
  gapAntecipacao: number;
  taxaAdmin: number;
  liquido: number;
  taxaSaque: number;         // operation_fee do TED
  valorSaque: number;        // net_total do TED (positivo)
  saldoFinal: number;        // saldo após o TED (ou saldo atual se sem TED)
}

const SaquePeriodSummary = ({ s, label }: { s: PeriodSummary; label: string }) => {
  const brutoSemJuros = s.bruto - s.juros;
  const sobranteAtual = s.saldoFinal;

  const cols: Array<{ label: string; value: number | null; color?: string }> = [
    { label: 'Bruto',                      value: s.bruto },
    { label: 'Sobra saldo anterior',        value: s.sobrantesInicio, color: s.sobrantesInicio !== 0 ? C.blue : undefined },
    { label: 'Sobra saldo atual',           value: sobranteAtual,     color: sobranteAtual !== 0 ? C.amber : undefined },
    { label: 'Juros Parcelamento',          value: s.juros > 0 ? -s.juros : 0 },
    { label: 'Bruto sem Juros',             value: brutoSemJuros },
    { label: 'Taxas de Venda',              value: s.taxaVenda > 0 ? -s.taxaVenda : 0, color: C.red },
    { label: 'Dif. Antecipação',            value: s.gapAntecipacao !== 0 ? -s.gapAntecipacao : 0, color: s.gapAntecipacao !== 0 ? C.red : undefined },
    { label: 'Taxas Administrativas',       value: s.taxaAdmin > 0 ? -s.taxaAdmin : 0, color: C.red },
    { label: 'Líquido',                     value: s.liquido, color: C.green },
    { label: 'Taxa de Saque',               value: s.taxaSaque > 0 ? -s.taxaSaque : 0, color: C.red },
    { label: 'Valor do Saque',              value: s.valorSaque > 0 ? -s.valorSaque : null, color: C.purple },
    { label: 'Saldo',                       value: sobranteAtual, color: sobranteAtual < 0 ? C.red : C.muted },
  ];

  return (
    <div style={{ padding: '12px 16px', background: '#F0F4FF', borderTop: `2px solid #C8D3E8` }}>
      <div style={{ fontSize: 11, fontWeight: 700, color: C.blue, marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
        Resumo — {label}
      </div>
      <div style={{ overflowX: 'auto' }}>
        <table style={{ borderCollapse: 'collapse', fontSize: 12, width: '100%', minWidth: 900 }}>
          <thead>
            <tr style={{ background: '#E8EEFA' }}>
              {cols.map(c => (
                <th key={c.label} style={{ padding: '6px 10px', fontWeight: 700, fontSize: 10, color: C.muted, textTransform: 'uppercase', letterSpacing: '0.05em', whiteSpace: 'nowrap', textAlign: 'right', borderBottom: `1px solid #C8D3E8` }}>
                  {c.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            <tr>
              {cols.map((c, i) => (
                <td key={i} style={{ padding: '8px 10px', fontWeight: 700, textAlign: 'right', fontVariantNumeric: 'tabular-nums', color: c.color ?? C.text, whiteSpace: 'nowrap' }}>
                  {c.value === null ? '—' : c.value === 0 ? '0,00' : fmt(Math.abs(c.value))}
                </td>
              ))}
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
};

const LinhaALinhaSection = ({
  finance,
  salesData,
  salesNotConciliated,
  onExportSaquePDF,
  onViewUserSales,
}: {
  finance: FinanceItem[];
  salesData: SaleItem[];
  salesNotConciliated: NotConciliatedSale[];
  onExportSaquePDF: (saqueIndex: number) => void;
  onViewUserSales: (clientId: string | number, clientName: string) => void;
}) => {
  // Mapa hash→SaleItem para lookup de client_id sem depender do campo no extrato
  const saleByHashMap = new Map<string, SaleItem>();
  for (const s of salesData) saleByHashMap.set(s.sale_hash, s);

  const [expandedRows, setExpandedRows] = useState<Set<number | string>>(new Set());
  const [collapsedPeriods, setCollapsedPeriods] = useState<Set<number>>(new Set());

  const toggleRow = (id: number | string) => {
    setExpandedRows(prev => {
      const next = new Set(prev); next.has(id) ? next.delete(id) : next.add(id); return next;
    });
  };
  const togglePeriod = (idx: number) => {
    setCollapsedPeriods(prev => {
      const next = new Set(prev); next.has(idx) ? next.delete(idx) : next.add(idx); return next;
    });
  };

  const ESTORNO_DEBITO  = new Set(['chargeback', 'refund']);
  const ESTORNO_CREDITO = new Set(['chargeback_refund']);

  // Ordena por dateRef — campo enviado pelo backend que representa a data de CRIAÇÃO
  // do registro (não de liquidação). Para TEDs, o backend usa movement_object.date_created
  // (quando o saque foi solicitado), enquanto `date` contém funding_date (quando liquidou).
  // Ordenar por `date` colocaria o TED depois de créditos posteriores ao saque, quebrando
  // o agrupamento por período. O Excel gerado pelo backend também usa dateRef para ordenar.
  // Fallback para `date` caso o backend não envie dateRef (endpoints legados).
  const allRows: Array<{ kind: 'finance'; data: FinanceItem; sortDate: number } | { kind: 'sale'; data: NotConciliatedSale; sortDate: number }> = [
    ...finance.map(f => ({
      kind: 'finance' as const,
      data: f,
      sortDate: new Date(f.dateRef ?? f.date).getTime(),
    })),
    ...salesNotConciliated
      .filter(s => !s.isPastDeadline)
      .map(s => ({
        kind: 'sale' as const, data: s,
        sortDate: s.estimatedAvailableAt?.getTime() ?? new Date(s.sale.approved_at ?? '').getTime(),
      })),
  ].sort((a, b) => a.sortDate - b.sortDate);

  // Saldo acumulado projetado
  let runningSaldo = 0;
  const rowsWithSaldo = allRows.map(row => {
    if (row.kind === 'finance') {
      const f = row.data as FinanceItem;
      if (f.operation_type_raw != null) runningSaldo = f.saldo ?? runningSaldo;
      return { ...row, saldoExibido: runningSaldo, isProjected: false };
    } else {
      runningSaldo += (row.data as NotConciliatedSale).sale.net_total ?? 0;
      return { ...row, saldoExibido: runningSaldo, isProjected: true };
    }
  });

  // Agrupa em períodos — cada período termina num TED (ou é o período final sem TED)
  type RowWithSaldo = typeof rowsWithSaldo[number];
  const periods: Array<{ rows: RowWithSaldo[]; hasSaque: boolean }> = [];
  let currentPeriod: RowWithSaldo[] = [];
  for (const row of rowsWithSaldo) {
    currentPeriod.push(row);
    if (row.kind === 'finance' && (row.data as FinanceItem).operation_type_raw === 'ted') {
      periods.push({ rows: currentPeriod, hasSaque: true });
      currentPeriod = [];
    }
  }
  if (currentPeriod.length > 0) {
    periods.push({ rows: currentPeriod, hasSaque: false });
  }

  // Saldo inicial de cada período = saldo após o TED do período anterior (ou 0)
  const periodStartSaldos: number[] = periods.map((_, i) => {
    if (i === 0) return 0;
    // Pega o saldo do último entry do período anterior
    const prevPeriod = periods[i - 1];
    const lastRow = prevPeriod.rows[prevPeriod.rows.length - 1];
    return lastRow.saldoExibido;
  });

  // Computa resumo por período
  const computeSummary = (period: { rows: RowWithSaldo[]; hasSaque: boolean }, startSaldo: number): PeriodSummary => {
    const credits = period.rows.filter(r => r.kind === 'finance' && (r.data as FinanceItem).operation_type_raw === 'credit');
    const tedRow  = period.rows.find(r => r.kind === 'finance' && (r.data as FinanceItem).operation_type_raw === 'ted');
    const ted     = tedRow ? (tedRow.data as FinanceItem) : null;

    const bruto        = credits.reduce((a, r) => a + ((r.data as FinanceItem).total ?? 0), 0);
    const juros        = credits.reduce((a, r) => a + ((r.data as FinanceItem).fees ?? 0), 0);
    const taxaVenda    = credits.reduce((a, r) => a + ((r.data as FinanceItem).tax ?? 0), 0);
    const gapAntec     = credits.reduce((a, r) => a + ((r.data as FinanceItem).antecipation_gap ?? 0), 0);
    const taxaAdmin    = credits.reduce((a, r) => a + ((r.data as FinanceItem).administration_tax ?? 0), 0);
    const liquido      = credits.reduce((a, r) => a + ((r.data as FinanceItem).net_total ?? 0), 0);
    const taxaSaque    = ted?.operation_fee ?? 0;
    const valorSaque   = ted ? Math.abs(ted.net_total ?? 0) : 0;
    const saldoFinal   = period.rows[period.rows.length - 1]?.saldoExibido ?? 0;

    return { bruto, sobrantesInicio: startSaldo, juros, taxaVenda, gapAntecipacao: gapAntec, taxaAdmin, liquido, taxaSaque, valorSaque, saldoFinal };
  };

  const lastSaqueDate = Math.max(0,
    ...finance.filter(f => f.operation_type_raw === 'ted').map(f => new Date(f.date).getTime())
  );

  let saqueIndex = 0;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      {/* Legenda */}
      <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', padding: '10px 0' }}>
        <LegendaDot color="#059669" label="Saque (TED)" />
        <LegendaDot color="#D97706" label="Não sacado (crédito pendente)" />
        <LegendaDot color="#6366F1" label="Aguardando conciliação" />
        <LegendaDot color="#C0392B" label="Chargeback / Estorno" />
        <LegendaDot color="#F97316" label="Estorno revertido" />
        <LegendaDot color="#94A3B8" label="Op. interna" />
      </div>

      {periods.map((period, pIdx) => {
        const isCollapsed = collapsedPeriods.has(pIdx);
        const startSaldo  = periodStartSaldos[pIdx];
        const summary     = computeSummary(period, startSaldo);
        const tedEntry    = period.rows.find(r => r.kind === 'finance' && (r.data as FinanceItem).operation_type_raw === 'ted');
        const tedDate     = tedEntry ? fmtDate((tedEntry.data as FinanceItem).date) : null;
        const firstDate   = period.rows.length > 0 && period.rows[0].kind === 'finance'
          ? fmtDate((period.rows[0].data as FinanceItem).date) : null;

        const periodLabel = tedDate
          ? `Período até saque de ${tedDate}`
          : `Período atual (sem saque)`;

        // Incrementa saque index para este período, se tiver TED
        const localSaqueIdx = period.hasSaque ? saqueIndex + 1 : null;
        if (period.hasSaque) saqueIndex += 1;

        return (
          <Card key={pIdx}>
            {/* Cabeçalho do accordion */}
            <div
              onClick={() => togglePeriod(pIdx)}
              style={{
                padding: '12px 18px', display: 'flex', alignItems: 'center', gap: 12, cursor: 'pointer',
                background: period.hasSaque ? C.blueLight : C.bg,
                borderBottom: isCollapsed ? 'none' : `1px solid ${C.border}`,
              }}
            >
              <div style={{ width: 4, height: 18, borderRadius: 2, background: period.hasSaque ? C.blue : C.muted, flexShrink: 0 }} />
              <span style={{ fontWeight: 700, fontSize: 13, color: C.text, flex: 1 }}>{periodLabel}</span>
              <div style={{ display: 'flex', gap: 20, alignItems: 'center' }}>
                <span style={{ fontSize: 12, color: C.muted }}>
                  {period.rows.filter(r => r.kind === 'finance' && (r.data as FinanceItem).operation_type_raw === 'credit').length} crédito(s) · {fmt(summary.bruto)} bruto
                </span>
                {period.hasSaque && (
                  <span style={{ fontSize: 12, fontWeight: 700, color: C.purple }}>Saque: {fmt(summary.valorSaque)}</span>
                )}
                <span style={{ fontSize: 13, color: C.muted }}>{isCollapsed ? '▼' : '▲'}</span>
              </div>
            </div>

            {/* Conteúdo do accordion */}
            {!isCollapsed && (
              <>
                <Table headers={['Aprovação', 'Conciliação', 'Operação', 'Cliente', 'Método', 'Bruto', 'Líquido', 'Saldo Acum.', 'Situação']}>
                  {period.rows.map((row, idx) => {
                    if (row.kind === 'finance') {
                      const item = row.data as FinanceItem;
                      const isSaque    = item.operation_type_raw === 'ted';
                      const isCB       = ESTORNO_DEBITO.has(item.operation_type_raw ?? '');
                      const isCBRefund = ESTORNO_CREDITO.has(item.operation_type_raw ?? '');
                      const isInternal = item.operation_type_raw == null;
                      const isNaoSacado = !isSaque && !isCB && !isCBRefund && !isInternal
                        && item.sacado === 0 && item.operation_type_raw === 'credit'
                        && new Date(item.date).getTime() > lastSaqueDate;

                      const rowBg = isSaque ? '#F0FDF4' : isCB ? '#FFF1F2' : isCBRefund ? '#FFF7ED' : isInternal ? '#F8FAFC' : isNaoSacado ? '#FFFBEB' : 'transparent';
                      const isCredit   = item.operation_type_raw === 'credit';
                      const isExpanded = expandedRows.has(item.item_id);

                      const opLabel = isSaque
                        ? <span style={{ color: '#059669', fontWeight: 700 }}>Saque (TED)</span>
                        : isCB
                          ? <span style={{ color: C.red, fontWeight: 700 }}>⚠️ {item.operation_type}</span>
                          : isCBRefund
                            ? <span style={{ color: '#F97316', fontWeight: 700 }}>↩️ {item.operation_type}</span>
                            : isInternal
                              ? <span style={{ color: '#64748B', fontStyle: 'italic' }}>🔧 {item.operation_type ?? 'Op. Interna'}</span>
                              : <span style={{ color: '#0369A1' }}>{item.operation_type}</span>;

                      const currentSaqueIndex = localSaqueIdx ?? 0;

                      const situLabel = isSaque
                        ? <Pill label="Sacado"     color="#065F46" bg="#D1FAE5" />
                        : isCB
                          ? <Pill label="Estorno"    color={C.red}   bg={C.redBg} />
                          : isCBRefund
                            ? <Pill label="Revertido"  color="#9A3412" bg="#FEF3C7" />
                            : isInternal
                              ? <Pill label="Aj. Adm."   color="#475569" bg="#F1F5F9" />
                              : isNaoSacado
                                ? <Pill label="Não sacado" color={C.amber} bg={C.amberBg} />
                                : <Pill label="Coberto"    color={C.muted} bg="#F1F5F9" />;

                      return (
                        <React.Fragment key={`f-${item.item_id}-${pIdx}-${idx}`}>
                          <tr style={{ background: rowBg, cursor: isCredit ? 'pointer' : 'default' }} onClick={() => isCredit && toggleRow(item.item_id)}>
                            <Td nowrap muted>{fmtDateTime(item.approved_at)}</Td>
                            <Td nowrap muted>{fmtDate(item.date)}</Td>
                            <Td>{opLabel}</Td>
                            <Td bold>{item.client ?? '—'}</Td>
                            <Td muted>{item.payment_method_id || '—'}</Td>
                            <Td align="right">{fmt(Math.abs(item.total ?? 0))}</Td>
                            <Td align="right" color={isSaque || isCB ? C.red : undefined} bold>
                              {isSaque || isCB ? `−${fmt(Math.abs(item.net_total))}` : fmt(item.net_total)}
                            </Td>
                            <Td align="right" bold>{fmt(row.saldoExibido)}</Td>
                            <Td>
                              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                                {situLabel}
                                {isSaque && (
                                  <FaFilePdf size={14} title={`Exportar PDF — Saque ${currentSaqueIndex}`}
                                    style={{ cursor: 'pointer', color: C.red, flexShrink: 0 }}
                                    onClick={e => { e.stopPropagation(); onExportSaquePDF(currentSaqueIndex); }}
                                  />
                                )}
                                {isCredit && <span style={{ fontSize: 10, color: C.muted }}>{isExpanded ? '▲' : '▼'}</span>}
                                {(() => {
                                  const saleRef = saleByHashMap.get(item.hash);
                                  const cid = (item as any).client_id ?? (saleRef as any)?.client_id ?? (saleRef as any)?.payer_id;
                                  const cname = item.client || saleRef?.payer_id_response?.name || saleRef?.client_id_response?.name || 'Cliente';
                                  if (!cid) return null;
                                  return (
                                    <button
                                      title="Ver histórico do cliente"
                                      onClick={e => { e.stopPropagation(); onViewUserSales(cid, cname); }}
                                      style={{ border: 'none', background: C.blueLight, cursor: 'pointer', padding: '3px 9px', borderRadius: 6, fontSize: 11, fontWeight: 700, color: C.blue, display: 'flex', alignItems: 'center', gap: 4 }}
                                    >
                                      👤 Ver compras
                                    </button>
                                  );
                                })()}
                              </div>
                            </Td>
                          </tr>

                          {isCredit && isExpanded && (
                            <tr key={`detail-${item.item_id}`}>
                              <td colSpan={9} style={{ padding: '8px 20px 14px 32px', background: '#FAFBFD', borderBottom: `1px solid ${C.border}` }}>
                                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '8px 20px', padding: '10px 14px', background: '#fff', borderRadius: 8, border: `1px solid ${C.border}`, fontSize: 12 }}>
                                  <Stat label="Bruto"               value={fmt(item.total ?? 0)}                    size="sm" accent={C.text} />
                                  <Stat label="Juros parcelamento"  value={`−${fmt(item.fees ?? 0)}`}               size="sm" accent={C.red} />
                                  <Stat label="Taxa de venda"       value={`−${fmt(item.tax ?? 0)}`}                size="sm" accent={C.red} />
                                  <Stat label="Taxa administrativa" value={`−${fmt(item.administration_tax ?? 0)}`} size="sm" accent={C.red} />
                                  <Stat label="Taxa de cartão"      value={`−${fmt(item.transaction_tax ?? 0)}`}    size="sm" accent={C.red} />
                                  <Stat label="Gap de antecipação"  value={`${item.antecipation_gap > 0 ? '+' : ''}${fmt(item.antecipation_gap ?? 0)}`} size="sm" accent={item.antecipation_gap > 0 ? C.red : item.antecipation_gap < 0 ? C.green : C.muted} />
                                  <Stat label="Líquido recebido"    value={fmt(item.net_total)}                     size="sm" accent={C.green} />
                                </div>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    }

                    // Venda aguardando conciliação
                    const { sale, estimatedAvailableAt } = row.data as NotConciliatedSale;
                    const cliente = sale.payer_id_response?.name ?? sale.client_id_response?.name ?? '—';
                    return (
                      <tr key={`s-${sale._id}-${pIdx}-${idx}`} style={{ background: '#F5F3FF' }}>
                        <Td nowrap muted>{fmtDateTime(sale.approved_at)}</Td>
                        <Td nowrap muted>
                          <span style={{ color: '#6366F1', fontSize: 11 }}>{estimatedAvailableAt ? fmtDate(estimatedAvailableAt.toISOString()) : '—'}</span>
                          <div style={{ fontSize: 9, color: '#A5B4FC' }}>estimado</div>
                        </Td>
                        <Td><span style={{ color: '#6366F1', fontWeight: 700 }}>🕐 Ag. Conciliação</span></Td>
                        <Td bold>{cliente}</Td>
                        <Td muted>{sale.payment_method_id}</Td>
                        <Td align="right">{fmt(sale.total)}</Td>
                        <Td align="right">{fmt(sale.net_total)}</Td>
                        <Td align="right">
                          <span style={{ color: '#6366F1', fontWeight: 700 }}>{fmt(row.saldoExibido)}</span>
                          <div style={{ fontSize: 9, color: '#A5B4FC' }}>projetado</div>
                        </Td>
                        <Td><Pill label="Aguardando" color="#5B21B6" bg="#EDE9FE" /></Td>
                      </tr>
                    );
                  })}
                </Table>

                {/* Resumo do período */}
                <SaquePeriodSummary s={summary} label={periodLabel} />
              </>
            )}
          </Card>
        );
      })}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
//  Componente principal
// ─────────────────────────────────────────────────────────────────────────────

type TabId = 'resumo' | 'nao_conciliadas' | 'divergencias' | 'linha_linha';

const FinanceReportComponent = () => {
  const { configuration, projectHash } = useConfiguration();
  const { addToast } = useToast();
  const { loadGlobal, salesData, financeData, report, loading } = useFinanceCheck();
  const [tab, setTab]       = useState<TabId>('resumo');
  const [showAI, setShowAI] = useState(false);
  const [drawer, setDrawer] = useState<{ clientId: string | number; clientName: string } | null>(null);
  const openDrawer = (clientId: string | number, clientName: string) => setDrawer({ clientId, clientName });
  const currentProjectUrl = configuration?.url ?? '';
 
  const {open} = useTransferPopup();
  useEffect(() => {
    if (configuration?.url) loadGlobal(configuration.url);
  }, [configuration?.url]);

  // ── Exportação PDF geral ────────────────────────────────────────────────
  const gerarPDF = async () => {
    addToast({ title: 'Gerando PDF…', type: 'info' });
    const project = (await api.get(`/projects`, { params: { where: { url: projectHash } } }))?.data?.rows?.[0];
    const response = await api.get(`/check-saldo-static/${project.url}/json`);
    handleFinanceReportPDF({ data: response?.data, project });
  };

  // ── Exportação PDF por saque ────────────────────────────────────────────
  const gerarPDFSaque = async (saque: number) => {
    addToast({ title: `Gerando PDF do saque ${saque}…`, type: 'info' });
    const project = (await api.get(`/projects`, { params: { where: { url: projectHash } } }))?.data?.rows?.[0];
    const response = await api.get(`/check-saldo-static/${project.url}/json`);
    handleFinanceReportPDFWithSaque({ data: response?.data, project, saque });
  };

  // ── Exportação Excel ────────────────────────────────────────────────────
  const gerarExcel = async () => {
    addToast({ title: 'Gerando Excel…', type: 'info' });
    await downloadit({ url: `/check-saldo-static/${projectHash}/xlsx` });
  };

  if (loading || !report) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: 260, gap: 14 }}>
        <div style={{ width: 38, height: 38, borderRadius: '50%', border: `3px solid ${C.border}`, borderTopColor: C.blue, animation: 'spin 0.8s linear infinite' }} />
        <span style={{ color: C.muted, fontSize: 14 }}>Carregando dados financeiros…</span>
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  const totalDivergencias = report.chargebacks.length;

  const tabs: { id: TabId; label: string; badge?: number }[] = [
    { id: 'resumo',          label: 'Relatório Geral' },
    { id: 'nao_conciliadas', label: 'Não Conciliadas',       badge: report.salesNotConciliated.length },
    { id: 'divergencias',    label: 'Divergências',           badge: totalDivergencias },
    { id: 'linha_linha',     label: 'Linha a Linha',         badge: financeData.length },
  ];

  return (
    <div style={{ fontFamily: "'DM Sans','Helvetica Neue',Arial,sans-serif", background: C.bg, minHeight: '100vh', padding: '28px 20px' }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700;800&display=swap');
        * { box-sizing: border-box; }
        tr:hover td { background: #F7F9FD; }
        @keyframes spin  { to { transform: rotate(360deg); } }
        @keyframes blink { 0%,100%{opacity:1} 50%{opacity:0} }
      `}</style>

      {/* Drawer de histórico do cliente */}
      {drawer && (
        <UserSalesDrawer
          clientId={drawer.clientId}
          clientName={drawer.clientName}
          currentProjectUrl={currentProjectUrl}
          onClose={() => setDrawer(null)}
        />
      )}

      {/* Painel de IA — fora do fluxo normal */}
      {showAI && (
        <AIAnalysisPanel
          report={report}
          financeData={financeData}
          onClose={() => setShowAI(false)}
        />
      )}

      <div style={{ maxWidth: 1200, margin: '0 auto', display: 'flex', flexDirection: 'column', gap: 20 }}>
        {/* Header */}
        <div>
          <h1 style={{ margin: 0, fontSize: 24, fontWeight: 800, color: C.text, letterSpacing: '-0.03em' }}>Relatório Financeiro</h1>
          <p style={{ margin: '4px 0 0', color: C.muted, fontSize: 13 }}>Conciliação e análise de valores arrecadados</p>
        </div>

        {/* Barra de tabs + botões de exportação */}
        <div style={{ display: 'flex', gap: 4, alignItems: 'center', flexWrap: 'wrap' }}>
          {/* Tabs */}
          <div style={{ display: 'flex', gap: 4, background: C.surface, border: `1px solid ${C.border}`, borderRadius: 10, padding: 4, flexWrap: 'wrap' }}>
            {tabs.map(t => (
              <TabBtn key={t.id} label={t.label} active={tab === t.id} badge={t.badge} onClick={() => setTab(t.id)} />
            ))}
          </div>

          {/* Separador visual */}
          <div style={{ width: 1, height: 32, background: C.border, margin: '0 4px' }} />

          {/* Botão PDF */}
          <button
            onClick={gerarPDF}
            title="Exportar relatório completo em PDF"
            style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '7px 14px', borderRadius: 8, border: `1px solid ${C.border}`, background: C.surface, color: C.red, fontWeight: 600, fontSize: 12, cursor: 'pointer' }}
          >
            <FaFilePdf size={14} /> PDF
          </button>

          {/* Botão Excel */}
          <button
            onClick={gerarExcel}
            title="Exportar relatório completo em Excel"
            style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '7px 14px', borderRadius: 8, border: `1px solid ${C.border}`, background: C.surface, color: C.green, fontWeight: 600, fontSize: 12, cursor: 'pointer' }}
          >
            <FaFileExcel size={14} /> Excel
          </button>

           
                             <button onClick={() => open(`${configuration?.url}`)} title="Exportar Excel"
                                    style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '7px 14px', borderRadius: 8, border: `1px solid #E3E8F0`, background: '#fff', color: '#0A7A55', fontWeight: 600, fontSize: 12, cursor: 'pointer' }}>
                                    <FaWallet size={14} /> Transferências
                                  </button>
                               

          {/* Botão Analisar com IA */}
          <button
            onClick={() => setShowAI(true)}
            title="Abrir painel de auditoria automática com IA"
            style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '7px 14px', borderRadius: 8, border: 'none', background: C.purple, color: '#fff', fontWeight: 700, fontSize: 12, cursor: 'pointer' }}
          >
            ⬇ JSON para auditoria
          </button>
        </div>

        {/* Conteúdo das abas */}
        {tab === 'resumo'          && (
          <ReportOverview
            r={report}
            finance={financeData}
            salesNotConciliated={report.salesNotConciliated}
            onExportSaquePDF={gerarPDFSaque}
          />
        )}
        {tab === 'nao_conciliadas' && <NotConciliatedSection items={report.salesNotConciliated} />}
        {tab === 'divergencias'    && (
          <DivergenceSection
            chargebacks={report.chargebacks}
            creditosOrfaos={report.creditosOrfaos}
            onViewUserSales={openDrawer}
          />
        )}
        {tab === 'linha_linha'     && (
          <LinhaALinhaSection
            finance={financeData}
            salesData={salesData}
            salesNotConciliated={report.salesNotConciliated}
            onExportSaquePDF={gerarPDFSaque}
            onViewUserSales={openDrawer}
          />
        )}
      </div>
    </div>
  );
};

export default FinanceReportComponent;
